import { useCallback, useMemo } from 'react';
import { trpc } from '../../trpc/react';
import { Metric, metricConfigDefaults, zWeightUnit } from '@strength/domain';
import { getPrescribableUnits } from '@strength/domain-workout';
import { sortByOrder } from '@strength/utils';
import { useViewer } from '../context/viewer';
import { useOrgScopedMetrics } from '../pages/admin/workout-import/OrgScopedMetricsContext';

/**
 * Preloads all metrics for the organization and provides cached access
 */
export function useMetrics(withSetsMetric = true) {
  const {
    team: { selectedTeam: teamId },
    org: {
      settings: { defaultWeightUnit: orgDefaultWeightUnit },
    },
  } = useViewer();

  const isBaseLibrary = teamId === '';

  // Admin pages render the sheet inside OrgScopedMetricsProvider, which supplies
  // the selected org's metrics directly (admin sessions can't call the
  // orgProcedure allMetrics endpoint). When that context is present, skip the
  // org-scoped query entirely; otherwise behave as before.
  const orgScoped = useOrgScopedMetrics();

  // Load all metrics in the background
  const query = trpc.metricsLibrary.allMetrics.useQuery(undefined, {
    staleTime: Infinity,
    enabled: orgScoped === null,
  });

  // Same admin-session gate as allMetrics above. The org-scoped provider
  // doesn't supply categories, so admin screens read them from the admin
  // router instead. Categories are few and effectively static within a
  // session, so cache them the same way.
  const categoriesQuery = trpc.metricsLibrary.categories.useQuery(undefined, {
    staleTime: Infinity,
    enabled: orgScoped === null,
  });

  // Use team settings when a specific team is selected, otherwise use org settings from viewer
  const teamDefaultWeightUnitQuery =
    trpc.teamSettings.defaultWeightUnit.useQuery(
      undefined,
      // use org settings when base library is selected
      { enabled: !isBaseLibrary && orgScoped === null },
    );

  const defaultWeightUnit = orgScoped
    ? // Admin page: the provider supplies the selected org's unit (the
      // teamSettings query below is disabled for admin sessions).
      orgScoped.defaultWeightUnit
    : isBaseLibrary
      ? orgDefaultWeightUnit
      : teamDefaultWeightUnitQuery.data;

  const metrics = useMemo(() => {
    const raw = orgScoped ? orgScoped.metrics : query.data;
    const sorted = sortByOrder(raw || []);
    if (!withSetsMetric) {
      return sorted.filter((m) => m.type !== 'sets');
    }
    return sorted;
  }, [orgScoped, query.data, withSetsMetric]);

  const getMetrics = useCallback(
    (showAll = false) => {
      if (showAll) {
        return metrics;
      }
      return metrics.filter((m) => !m.recordType);
    },
    [metrics],
  );

  const getMetric = useCallback(
    (id: string) => {
      if (!metrics.length) {
        // metrics haven't loaded yet
        // this gets quickly overwritten by re-renders once the data is available
        const defaultMetric: Metric = {
          id: '',
          isCollectable: false,
          name: '',
          sortOrder: 0,
          type: 'custom',
          config: metricConfigDefaults(),
        };
        return defaultMetric;
      }
      const metric = metrics.find((m) => m.id === id);
      if (!metric) {
        throw new Error(`Could not find metric with id ${id}`);
      }

      return metric;
    },
    [metrics],
  );

  // A non-null handler on the sets metric. id may not be populated right away
  const setsMetric = useMemo(() => {
    const sm = metrics.find((m) => m.type === 'sets');

    if (!sm) {
      return {
        id: '',
        isCollectable: false,
        name: 'Sets',
        sortOrder: -100,
        type: 'sets' as const,
        config: metricConfigDefaults(),
      };
    }

    return sm;
  }, [metrics]);

  const getUnitsForMetric = useCallback(
    (id: string, forActuals = false) => {
      const metric = getMetric(id);

      if (metric.type === 'intensity') {
        if (!defaultWeightUnit) {
          return [];
        }

        const weightUnits = [
          defaultWeightUnit,
          ...zWeightUnit.options.filter((unit) => unit !== defaultWeightUnit),
        ];
        // Intensity has always accepted a typed '%' (transformIntensity), so
        // offer it when prescribing, same as any other percent-prescribable
        // metric. Appended, not prepended, so the default unit stays first.
        return forActuals ? weightUnits : [...weightUnits, '%'];
      }

      return getPrescribableUnits(metric.config, forActuals);
    },
    [defaultWeightUnit, getMetric],
  );

  const rehydrate = useCallback(() => {
    query.refetch();
  }, [query]);

  const categories = useMemo(() => categoriesQuery.data ?? [], [
    categoriesQuery.data,
  ]);

  const categoryNamesById = useMemo(
    () =>
      new Map(
        categories.map((category) => [category.id, category.name] as const),
      ),
    [categories],
  );

  // Resolves a metric's categoryId to its display name. Returns undefined for
  // uncategorized metrics and while the category list hasn't loaded, so
  // callers decide how to render the empty case.
  const getCategoryName = useCallback(
    (categoryId?: string | null) =>
      categoryId ? categoryNamesById.get(categoryId) : undefined,
    [categoryNamesById],
  );

  return {
    categories,
    getCategoryName,
    getMetrics,
    defaultWeightUnit: defaultWeightUnit ?? 'lbs',
    getMetric,
    getUnitsForMetric,
    // When an admin page supplies org-scoped metrics via context, loading is
    // driven by that provider's queries (the org-scoped allMetrics query is
    // disabled). Fall back to the org-world query's loading state otherwise.
    isLoading: orgScoped ? orgScoped.isLoading : query.isLoading,
    error: query.error?.message,
    rehydrate,
    setsMetric,
  };
}
